import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  PlaygroundPreview, 
  checkPlaygroundPreviewHealth,
  startPlaygroundRecording,
  stopPlaygroundRecording,
  submitPlaygroundSolution,
  deletePlaygroundPreview,
  getPlaygroundPreviewStatus
} from '../api';
import { EventRecorder } from '../utils/event-recorder';
import { VideoRecorder } from '../utils/video-recorder';
import { useDynamicScale } from '../hooks/useDynamicScale';
import './PlaygroundRecordingModal.css';

interface PlaygroundRecordingModalProps {
  isOpen: boolean;
  onClose: () => void;
  playgroundPreview: PlaygroundPreview | null;
  onPlaygroundPreviewUpdate: (preview: PlaygroundPreview) => void;
  software: string;
  problemId: string;
  problemName: string;
  taskDescription: string;
  projectId?: string;
  onSolutionSubmitted: () => void;
  isImpossibleTask?: boolean;
}

const PlaygroundRecordingModal: React.FC<PlaygroundRecordingModalProps> = ({
  isOpen,
  onClose,
  playgroundPreview,
  onPlaygroundPreviewUpdate,
  software,
  problemId,
  problemName,
  taskDescription,
  projectId,
  onSolutionSubmitted,
  isImpossibleTask = false
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isStartingRecording, setIsStartingRecording] = useState(false);
  const [recordingId, setRecordingId] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState<Date | null>(null);
  const [recordingElapsedTime, setRecordingElapsedTime] = useState<string>('');
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [isHealthy, setIsHealthy] = useState(false);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const [showLoginInfo, setShowLoginInfo] = useState(false);
  const [copiedField, setCopiedField] = useState<'username' | 'password' | null>(null);
  const [videoRecordingError, setVideoRecordingError] = useState<string | null>(null);
  const [isVideoRecording, setIsVideoRecording] = useState(false);
  const [showVideoRecordingPrompt, setShowVideoRecordingPrompt] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [isStoppingRecording, setIsStoppingRecording] = useState(false);
  const [showImpossibleTaskModal, setShowImpossibleTaskModal] = useState(false);
  const [impossibleTaskReason, setImpossibleTaskReason] = useState('');
  
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const recorderRef = useRef<EventRecorder>(new EventRecorder());
  const videoRecorderRef = useRef<VideoRecorder | null>(null);
  const healthCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loginButtonRef = useRef<HTMLButtonElement>(null);

  // Dynamic scaling for playground iframe
  const { scale, containerDimensionsEm } = useDynamicScale({
    originalWidth: 1400,
    originalHeight: 850,
    minScale: 0.5,
    maxScale: 1.2,
    padding: 40 // Standard padding for proper scaling
  });

  // Get login info for supported software
  const getLoginInfo = (software: string): { username: string; password: string } | null => {
    switch (software.toLowerCase()) {
      case 'immich':
        return {
          username: 'emmett.miller@gmail.com',
          password: 'password'
        };
      case 'odoo':
        return {
          username: 'admin',
          password: 'admin'
        };
      case 'erpnext':
        return {
          username: 'administrator',
          password: 'Jasdbj@!sa8aX2!d'
        };
      default:
        return null;
    }
  };

  const loginInfo = getLoginInfo(software);

  // Handle copy functionality
  const handleCopy = (text: string, field: 'username' | 'password') => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Timer for recording duration
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording && recordingStartTime) {
      interval = setInterval(() => {
        const elapsed = Date.now() - recordingStartTime.getTime();
        const minutes = Math.floor(elapsed / 60000);
        const seconds = Math.floor((elapsed % 60000) / 1000);
        setRecordingElapsedTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }, 100);
    } else {
      setRecordingElapsedTime('');
    }
    return () => clearInterval(interval);
  }, [isRecording, recordingStartTime]);

  // Setup iframe event listeners
  useEffect(() => {
    if (!isOpen || !iframeRef.current) return;

    const iframe = iframeRef.current;
    
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow) return;
      
      if (event.data.type === 'playground-event' && event.data.event) {
        const correctedEvent = {
          ...event.data.event,
          timestamp: recorderRef.current.getElapsedTime()
        };
        recorderRef.current.addEvent(correctedEvent);
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsRecording(false);
      setRecordingStartTime(null);
      setIframeLoaded(false);
      setIsHealthy(false);
      setIsCheckingHealth(false);
      setShowLoginInfo(false);
      setDropdownPosition(null);
      setCopiedField(null);
    }
  }, [isOpen]);

  // Start health checking when playground is ready
  useEffect(() => {
    const playgroundId = playgroundPreview?.id || playgroundPreview?.playground_id;
    // Only start health checks if the playground is actually ready
    if (isOpen && playgroundPreview?.status === 'ready' && playgroundId && !isHealthy) {
      setIsCheckingHealth(true);
      startHealthCheck(playgroundId);
    } else if (playgroundPreview?.status === 'creating') {
      // Reset health state if playground is still creating
      setIsHealthy(false);
      setIsCheckingHealth(false);
    }

    return () => {
      if (healthCheckTimeoutRef.current) {
        clearTimeout(healthCheckTimeoutRef.current);
        healthCheckTimeoutRef.current = null;
      }
    };
  }, [isOpen, playgroundPreview, isHealthy]);

  // Removed auto-start recording - user must manually click Start Recording

  // Health checking function
  const startHealthCheck = async (id: string) => {
    const maxHealthCheckAttempts = 24;
    let attempts = 0;

    const checkHealth = async () => {
      try {
        const healthResponse = await checkPlaygroundPreviewHealth(id);
        
        if (healthResponse.status === 'healthy') {
          setIsHealthy(true);
          setIsCheckingHealth(false);
          healthCheckTimeoutRef.current = null;
          return;
        } else if (healthResponse.status === 'failed') {
          setIsCheckingHealth(false);
          healthCheckTimeoutRef.current = null;
          return;
        }

        attempts++;
        if (attempts >= maxHealthCheckAttempts) {
          setIsCheckingHealth(false);
          healthCheckTimeoutRef.current = null;
          return;
        }

        healthCheckTimeoutRef.current = setTimeout(checkHealth, 5000);
      } catch (error) {
        console.error('Health check failed:', error);
        attempts++;
        if (attempts < maxHealthCheckAttempts) {
          healthCheckTimeoutRef.current = setTimeout(checkHealth, 5000);
        } else {
          setIsCheckingHealth(false);
          healthCheckTimeoutRef.current = null;
        }
      }
    };

    await checkHealth();
  };

  const handleStartRecording = async () => {
    const playgroundId = playgroundPreview?.id || playgroundPreview?.playground_id;
    if (!playgroundId) return;

    setIsStartingRecording(true);
    try {
      const result = await startPlaygroundRecording(playgroundId);
      setRecordingId(result.recording_id);
      
      recorderRef.current.start();
      setIsRecording(true);
      setRecordingStartTime(new Date());
      
      // Start video recording for supported software
      const videoEnabledSoftware = ['erpnext', 'odoo', 'immich'];
      if (videoEnabledSoftware.includes(software.toLowerCase()) && VideoRecorder.isSupported()) {
        setShowVideoRecordingPrompt(true);
      }
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Failed to start recording. Please try again.');
    } finally {
      setIsStartingRecording(false);
    }
  };

  const handleStopRecording = async () => {
    const playgroundId = playgroundPreview?.id || playgroundPreview?.playground_id;
    if (!playgroundId) return;

    // Stop recording and immediately show submission modal
    const events = recorderRef.current.stop();
    setIsRecording(false);
    setRecordingStartTime(null);
    setShowSubmissionModal(true);
    setIsStoppingRecording(true);
    
    let videoBlob: Blob | undefined;
    if (isVideoRecording && videoRecorderRef.current) {
      try {
        videoBlob = await videoRecorderRef.current.stopRecording();
        setIsVideoRecording(false);
      } catch (error) {
        console.error('Failed to stop video recording:', error);
        setVideoRecordingError(error instanceof Error ? error.message : 'Failed to stop video recording');
      }
    }
    
    try {
      // End the recording on the server
      await stopPlaygroundRecording(playgroundId, videoBlob, events);
      // Recording has finished processing, enable submit button
      setIsStoppingRecording(false);
    } catch (error) {
      console.error('Failed to stop recording:', error);
      alert('Failed to stop recording. Please try again.');
      setShowSubmissionModal(false);
      setIsStoppingRecording(false);
    }
  };

  const handleSubmitImpossibleTask = async () => {
    if (!impossibleTaskReason.trim()) {
      alert('Please provide an explanation for why this task is impossible');
      return;
    }

    setIsSubmitting(true);
    try {
      const playgroundId = playgroundPreview?.id || playgroundPreview?.playground_id || 'impossible-task';
      await submitPlaygroundSolution({
        problem_id: problemId,
        solution_project_id: projectId,
        playground_session_id: playgroundId,
        recording_id: 'impossible-task',
        submission_notes: `IMPOSSIBLE TASK: ${impossibleTaskReason.trim()}`
      });

      onSolutionSubmitted();
      onClose();
      setShowImpossibleTaskModal(false);
      setImpossibleTaskReason('');
    } catch (error) {
      console.error('Error submitting impossible task:', error);
      alert('Failed to submit impossible task explanation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitSolution = async () => {
    const playgroundId = playgroundPreview?.id || playgroundPreview?.playground_id;
    if (!playgroundId || !recordingId) return;

    setIsSubmitting(true);
    try {
      await submitPlaygroundSolution({
        problem_id: problemId,
        solution_project_id: projectId,
        playground_session_id: playgroundId,
        recording_id: recordingId,
        submission_notes: submissionNotes
      });
      
      onSolutionSubmitted();
      onClose();
    } catch (error) {
      console.error('Failed to submit solution:', error);
      alert('Failed to submit solution. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelRecording = async () => {
    const playgroundId = playgroundPreview?.id || playgroundPreview?.playground_id;
    
    // Stop client-side recording
    if (isRecording || recordingId) {
      recorderRef.current.stop();
      
      // Stop video recording if active
      if (isVideoRecording && videoRecorderRef.current) {
        try {
          await videoRecorderRef.current.stopRecording();
          setIsVideoRecording(false);
        } catch (error) {
          console.error('Failed to stop video recording on cancel:', error);
        }
      }
      
      // Delete the playground to clean up everything (much faster than end-record)
      if (playgroundId) {
        try {
          await deletePlaygroundPreview(playgroundId);
        } catch (error) {
          console.error('Failed to delete playground on cancel:', error);
          // Continue anyway - user wants to exit
        }
      }
    }
    
    // Reset all recording states
    setIsRecording(false);
    setRecordingStartTime(null);
    setRecordingId(null);
    setShowExitConfirmation(false);
    setIsStartingRecording(false);
    
    onClose();
  };

  const handleResetPlayground = async () => {
    const playgroundId = playgroundPreview?.id || playgroundPreview?.playground_id;
    if (!playgroundId) return;

    try {
      setIsResetting(true);
      setShowResetConfirmation(false);
      
      // Stop any ongoing recording
      if (isRecording) {
        recorderRef.current.stop();
        setIsRecording(false);
        setRecordingStartTime(null);
        setRecordingId(null);
        
        if (isVideoRecording && videoRecorderRef.current) {
          try {
            await videoRecorderRef.current.stopRecording();
            setIsVideoRecording(false);
          } catch (error) {
            console.error('Failed to stop video recording on reset:', error);
          }
        }
      }
      
      // Delete the current playground
      await deletePlaygroundPreview(playgroundId);
      
      // Reset all states
      setIsHealthy(false);
      setIsCheckingHealth(false);
      setIframeLoaded(false);
      
      // Create a new playground
      let newPreview = await getPlaygroundPreviewStatus(software, problemId);
      
      // Poll if status is "creating"
      const maxAttempts = 60; // 5 minutes max
      let attempts = 0;
      
      while (newPreview.status === 'creating' && attempts < maxAttempts) {
        console.log('[Playground Reset] Status: creating, polling...', { attempt: attempts + 1 });
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        try {
          newPreview = await getPlaygroundPreviewStatus(software, problemId);
        } catch (pollError) {
          console.error('[Playground Reset] Polling error:', pollError);
        }
        
        attempts++;
      }
      
      if (newPreview.status === 'failed') {
        throw new Error('Failed to create new playground');
      }
      
      if (newPreview.status === 'creating' && attempts >= maxAttempts) {
        throw new Error('Playground creation timed out');
      }
      
      // Update the playground preview in parent component
      onPlaygroundPreviewUpdate(newPreview);
      
    } catch (error) {
      console.error('Failed to reset playground:', error);
      alert('Failed to reset playground. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const getIframeSrc = () => {
    if (!playgroundPreview?.access_url) return '';
    
    const match = playgroundPreview.access_url.match(/https?:\/\/([^.]+)\.([^.]+)\.modal\.host/);
    if (match && match[1] && match[2]) {
      const subdomain = `playground-${match[1]}-${match[2]}`;
      let path = '';
      if (software.toLowerCase() === 'immich') {
        path = '/photos';
      }
      return `https://${subdomain}.habitat.inc${path}`;
    }
    return playgroundPreview.access_url;
  };

  if (!isOpen || !playgroundPreview) return null;

  return (
    <div className="playground-recording-modal-backdrop" onClick={handleBackdropClick}>
      <div className={`playground-recording-modal ${isRecording ? 'recording' : ''}`} onClick={(e) => e.stopPropagation()}>
        <button 
          className="recording-close-button"
          onClick={() => setShowExitConfirmation(true)}
          aria-label="Close recording"
        >
          ✕
        </button>
        <div className="modal-scroll-content">
          <div className={`recording-header ${isHeaderCollapsed ? 'collapsed' : ''}`}>
            <div className="header-top-row">
              <div className="recording-info">
                <h2>
                  {isRecording && (
                    <span className="recording-indicator">
                      <span className="recording-dot"></span>
                    </span>
                  )}
                  {isRecording ? 'Recording Solution' : isStartingRecording ? 'Initializing Recording' : 'Preparing to Record'}
                  {isVideoRecording && (
                    <span className="video-recording-badge" title="Video recording in progress">
                      🎥
                    </span>
                  )}
                </h2>
                {!isRecording && !isStartingRecording && playgroundPreview?.status === 'ready' && isHealthy && iframeLoaded && (
                  <p className="recording-subtitle">
                    The playground is loaded with the problem's start state. When ready, click "Start Recording" to begin.
                  </p>
                )}
                {isRecording && (
                  <>
                    <span className="recording-timer">{recordingElapsedTime}</span>
                    {videoRecordingError && (
                      <span className="video-error-message" title={videoRecordingError}>
                        ⚠️ Video recording failed
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="header-right-controls">
                <div className="recording-controls">
                  {!isRecording ? (
                    <>
                      <button
                        className="reset-playground-button"
                        onClick={() => setShowResetConfirmation(true)}
                        disabled={isResetting || isStartingRecording || playgroundPreview?.status !== 'ready' || !isHealthy}
                        title="Reset playground to start state"
                      >
                        {isResetting ? (
                          <>
                            <div className="button-spinner"></div>
                            Resetting...
                          </>
                        ) : (
                          <>
                            🔄 Reset Playground
                          </>
                        )}
                      </button>
                      <button
                        className="start-recording-button"
                        onClick={handleStartRecording}
                        disabled={isStartingRecording || playgroundPreview?.status !== 'ready' || !isHealthy || !iframeLoaded}
                      >
                        {isStartingRecording ? (
                          <>
                            <div className="button-spinner"></div>
                            Initializing...
                          </>
                        ) : (
                          <>
                            <span className="record-icon">🔴</span>
                            Start Recording
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      className="stop-recording-button"
                      onClick={handleStopRecording}
                      disabled={!isRecording}
                    >
                      ⏹ End Recording
                    </button>
                  )}
                  
                  {/* Impossible Task Button - show when not recording and task is marked as impossible */}
                  {!isRecording && !isStartingRecording && playgroundPreview?.status === 'ready' && isHealthy && isImpossibleTask && (
                    <button
                      className="impossible-task-button-modal"
                      onClick={() => setShowImpossibleTaskModal(true)}
                      style={{ marginLeft: '1rem' }}
                    >
                      Explain Impossible Task
                    </button>
                  )}
                </div>
                {!isHeaderCollapsed && loginInfo && playgroundPreview?.status === 'ready' && isHealthy && iframeLoaded && (
                  <div className="login-info-container">
                    <button
                      ref={loginButtonRef}
                      className="action-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const newShowState = !showLoginInfo;
                        setShowLoginInfo(newShowState);
                        
                        if (newShowState && loginButtonRef.current) {
                          const rect = loginButtonRef.current.getBoundingClientRect();
                          setDropdownPosition({ 
                            top: rect.bottom + 8 + window.scrollY, 
                            left: rect.left + window.scrollX 
                          });
                        } else {
                          setDropdownPosition(null);
                        }
                      }}
                      title="Show login credentials"
                    >
                      Show Login Info
                    </button>
                    {showLoginInfo && dropdownPosition && createPortal(
                      <>
                        <div 
                          style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 2147483646,
                            background: 'transparent'
                          }}
                          onClick={() => {
                            setShowLoginInfo(false);
                            setDropdownPosition(null);
                          }}
                        />
                        <div 
                          className="login-info-dropdown"
                          style={{
                            top: `${dropdownPosition?.top || 0}px`,
                            left: `${dropdownPosition?.left || 0}px`,
                            position: 'fixed',
                            zIndex: 2147483647
                          }}
                          onClick={(e) => e.stopPropagation()}>
                          <h3>{software} Login Credentials</h3>
                          <div className="credential-row">
                            <p><strong>Username:</strong> <code>{loginInfo.username}</code></p>
                            <button
                              className="copy-button-inline"
                              onClick={() => handleCopy(loginInfo.username, 'username')}
                              title={copiedField === 'username' ? 'Copied!' : 'Copy username'}
                            >
                              {copiedField === 'username' ? '✓' : '📋'}
                            </button>
                          </div>
                          <div className="credential-row">
                            <p><strong>Password:</strong> <code>{loginInfo.password}</code></p>
                            <button
                              className="copy-button-inline"
                              onClick={() => handleCopy(loginInfo.password, 'password')}
                              title={copiedField === 'password' ? 'Copied!' : 'Copy password'}
                            >
                              {copiedField === 'password' ? '✓' : '📋'}
                            </button>
                          </div>
                        </div>
                      </>,
                      document.body
                    )}
                  </div>
                )}
                <button 
                  className="collapse-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsHeaderCollapsed(!isHeaderCollapsed);
                    // When collapsing, also hide the login info dropdown
                    if (!isHeaderCollapsed) {
                      setShowLoginInfo(false);
                      setDropdownPosition(null);
                    }
                  }}
                  aria-label={isHeaderCollapsed ? 'Expand header' : 'Collapse header'}
                  title={isHeaderCollapsed ? 'Expand header' : 'Collapse header'}
                >
                  <svg 
                    className={`collapse-icon ${isHeaderCollapsed ? 'collapsed' : ''}`}
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </div>
            </div>
            {!isHeaderCollapsed && (
              <div className="task-description-section">
                <p><strong>Problem:</strong> {problemName}</p>
                <p><strong>Task:</strong> {taskDescription}</p>
              </div>
            )}
          </div>
          <div className="playground-iframe-container" style={{ 
            width: containerDimensionsEm.width, 
            height: containerDimensionsEm.height 
          }}>
            {(playgroundPreview?.status === 'creating' || isCheckingHealth || !isHealthy || !iframeLoaded) && (
              <div className="iframe-loading-skeleton">
                <div className="loading-text">
                  {playgroundPreview?.status === 'creating' ? `Creating ${software} playground...` :
                   isCheckingHealth ? `Loading ${software} playground...` : 
                   !isHealthy ? `Starting ${software}...` :
                   'Loading...'}
                </div>
              </div>
            )}
            <iframe
              ref={iframeRef}
              src={isHealthy && playgroundPreview?.status === 'ready' ? getIframeSrc() : 'about:blank'}
              title={`${software} Solution Recording`}
              className={`playground-recording-iframe ${!isHealthy || !iframeLoaded ? 'hidden' : ''}`}
              style={{ transform: `scale(${scale})` }}
              allowFullScreen
              onLoad={() => {
                if (isHealthy && playgroundPreview?.status === 'ready' && iframeRef.current?.src !== 'about:blank') {
                  setIframeLoaded(true);
                }
              }}
            />
          </div>
        </div>
      </div>
      
      {/* Exit Confirmation Modal */}
      {showExitConfirmation && (
        <div className="exit-confirmation-backdrop">
          <div className="exit-confirmation-modal">
            <h3>Exit Recording?</h3>
            <p>
              {isRecording || recordingId ? 
                "Are you sure you want to exit? Your current recording will be cancelled and all captured actions will be lost." :
                "Are you sure you want to exit the recording modal?"
              }
            </p>
            <div className="exit-confirmation-buttons">
              <button 
                className="exit-cancel-button"
                onClick={() => setShowExitConfirmation(false)}
              >
                {isRecording || recordingId ? 'Continue Recording' : 'Stay'}
              </button>
              <button 
                className="exit-confirm-button"
                onClick={cancelRecording}
              >
                {isRecording || recordingId ? 'Exit & Cancel Recording' : 'Exit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Impossible Task Modal */}
      {showImpossibleTaskModal && (
        <div className="exit-confirmation-backdrop">
          <div className="exit-confirmation-modal impossible-task-modal-web">
            <h3>Explain Impossible Task</h3>
            <p>Please explain why this task cannot be completed as described:</p>
            <textarea
              className="submission-notes-textarea"
              value={impossibleTaskReason}
              onChange={(e) => setImpossibleTaskReason(e.target.value)}
              placeholder="Explain why the task is impossible (e.g., missing information, contradictory requirements, technical limitations)..."
              rows={6}
              autoFocus
            />
            <div className="exit-modal-buttons">
              <button 
                className="exit-cancel-button"
                onClick={() => {
                  setShowImpossibleTaskModal(false);
                  setImpossibleTaskReason('');
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                className="exit-confirm-button"
                onClick={handleSubmitImpossibleTask}
                disabled={isSubmitting || !impossibleTaskReason.trim()}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Explanation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirmation && (
        <div className="reset-confirmation-backdrop">
          <div className="reset-confirmation-modal">
            <h3>🔄 Reset Playground?</h3>
            <p>
              This will reset the playground to a fresh start state. Any current progress will be lost, 
              and any ongoing recording will be cancelled.
            </p>
            <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
              This is useful if you made a mistake and want to start over from the beginning.
            </p>
            <div className="reset-confirmation-buttons">
              <button 
                className="reset-cancel-button"
                onClick={() => setShowResetConfirmation(false)}
              >
                Cancel
              </button>
              <button 
                className="reset-confirm-button"
                onClick={handleResetPlayground}
                disabled={isResetting}
              >
                {isResetting ? 'Resetting...' : 'Reset Playground'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Video Recording Prompt Modal */}
      {showVideoRecordingPrompt && (
        <div className="exit-confirmation-backdrop">
          <div className="exit-confirmation-modal video-recording-prompt">
            <h3>Enable Video Recording</h3>
            <p>
              For {software} solutions, we also record a video to help verify your solution.
            </p>
            <p className="video-prompt-instruction">
              The browser will ask for permission to record this tab. Simply click <strong>"Share"</strong> when prompted.
            </p>
            <div className="exit-confirmation-buttons">
              <button 
                className="exit-confirm-button video-start-button"
                onClick={async () => {
                  setShowVideoRecordingPrompt(false);
                  try {
                    setVideoRecordingError(null);
                    setIsVideoRecording(true);
                    videoRecorderRef.current = new VideoRecorder({
                      mimeType: 'video/webm;codecs=vp9',
                      videoBitsPerSecond: 500000 // Reduced to 500 Kbps for smaller file size
                    });
                    await videoRecorderRef.current.startRecording();
                  } catch (error) {
                    console.error('Failed to start video recording:', error);
                    setVideoRecordingError(error instanceof Error ? error.message : 'Failed to start video recording');
                    setIsVideoRecording(false);
                  }
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Solution Submission Modal */}
      {showSubmissionModal && (
        <div className="exit-confirmation-backdrop">
          <div className="exit-confirmation-modal submission-modal">
            <h3>Submit Solution</h3>
            {isStoppingRecording ? (
              <>
                <p>Finalizing your recording, please wait...</p>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                  <div className="button-spinner" style={{ width: '24px', height: '24px', borderColor: 'rgba(59, 130, 246, 0.3)', borderTopColor: '#3b82f6' }}></div>
                </div>
              </>
            ) : (
              <>
                <p>Your recording is complete. Add any notes about your solution (optional):</p>
                <textarea
                  className="submission-notes"
                  placeholder="Describe your approach, any challenges faced, or additional context..."
                  value={submissionNotes}
                  onChange={(e) => setSubmissionNotes(e.target.value)}
                  rows={4}
                />
              </>
            )}
            <div className="exit-confirmation-buttons">
              <button 
                className="exit-cancel-button"
                onClick={() => {
                  setShowSubmissionModal(false);
                  setSubmissionNotes('');
                }}
                disabled={isSubmitting || isStoppingRecording}
              >
                Cancel
              </button>
              <button 
                className="exit-confirm-button"
                onClick={handleSubmitSolution}
                disabled={isSubmitting || isStoppingRecording}
              >
                {isStoppingRecording ? 'Processing...' : isSubmitting ? 'Submitting...' : 'Submit Solution'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaygroundRecordingModal;